import type { ExperiencePage, SessionAdvance, SessionView } from '@matter_extractor/shared';
import { feedbackPageOf, feedbackThanksOf } from '@matter_extractor/shared';

/**
 * Where a session stands against the FEEDBACK page (retour-experience.md §5,
 * §6.2; issue #176) - the three questions the player asks, answered off the view
 * and nothing else.
 *
 * Pure, and in a file of its own so the three are read without a DOM: the
 * player composes them, and `session-ending.tsx` draws the button the first one
 * decides.
 */

/** The person is ON the feedback page - to answer it, or to read its thanks. */
export function onFeedbackPage(page: ExperiencePage | null): boolean {
  return page?.kind === 'feedback';
}

/**
 * « Donner mon avis » is offered (§6.2): the engine CLOSED the session, the
 * frozen experience has a feedback page, and the person has not been taken to
 * it yet. On the feedback page itself the closing panel gives way to the page
 * (or to its thanks); without a feedback page the panel is the one of before
 * #176, « la phrase d'excuse, et rien d'autre ». An expired session is never
 * offered anything (§6.3, H2).
 */
export function feedbackOffered(view: SessionView, page: ExperiencePage | null): boolean {
  return (
    view.status === 'closed' && !onFeedbackPage(page) && feedbackPageOf(view.content.pages) !== null
  );
}

/**
 * The phrase of thanks to show in place of the page's blocks, or null while the
 * page is to be played (§5). The view says the feedback was sent - a page
 * reopened after the send shows the thanks at once - and only on the feedback
 * page: a `feedbackSubmittedAt` found anywhere else is not a reason to hide a
 * page.
 */
export function feedbackThanksOfView(
  view: SessionView,
  page: ExperiencePage | null,
): string | null {
  if (page === null || !onFeedbackPage(page)) return null;
  return (view.feedbackSubmittedAt ?? null) === null ? null : feedbackThanksOf(page);
}

/**
 * The advance « Donner mon avis » posts (§6.2): the page the session stands on,
 * no value, no button - the server admits, on a `closed` session, ONE advance
 * and it leads to the page of retour.
 *
 * ONE place spells this request. It is a HYPOTHESIS of lot 2 about the server of
 * lot 1 (the existing advance carries the move), and the day the integration
 * finds it posts elsewhere, this function and the route's `post` are all that
 * move.
 */
export function openFeedbackAdvance(pageId: string): SessionAdvance {
  return { pageId, values: {}, pressedBlockId: null };
}
